import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import usePermissions from '@/hooks/permissions/usePermissions'
import { useDefaultTerritories, useQueryDefaults } from '@/hooks/queries/defaults/useQueryDefaults'
import {
  PaymentReviewOperation,
  paymentReviewExportQueryOptions,
  paymentReviewReportQueryOptions,
  paymentReviewSearchSchema,
  usePaymentReviewOrders,
} from '@/hooks/queries/usePaymentReview'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { createFileRoute } from '@tanstack/react-router'
import { RowSelectionState } from '@tanstack/react-table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PaymentReviewActionDialog,
  PaymentReviewTarget,
} from './-components/PaymentReviewActionDialog'
import { PaymentReviewDataTable } from './-components/PaymentReviewDataTable'
import { PaymentReviewUploadButton } from './-components/PaymentReviewUploadButton'

export const Route = createFileRoute('/_auth/_modules/_app/orders/payment-review/')({
  component: PaymentReviewPage,
  validateSearch: (search) => paymentReviewSearchSchema.parse(search),
})

function PaymentReviewPage() {
  const { t } = useTranslation('orders')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })
  const defaultTerritories = useDefaultTerritories()
  const { canUser } = usePermissions()

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [action, setAction] = useState<{
    operation: PaymentReviewOperation
    orders: PaymentReviewTarget[]
  } | null>(null)

  // Orders that have been released or cancelled keep the flag, so they stay reachable here by
  // changing the PTR status filter. The screen just defaults to the ones still awaiting review.
  useQueryDefaults({
    search,
    navigate,
    defaults: {
      ptrStatus: ['UNDER REVIEW'],
      territory: defaultTerritories,
    },
  })

  const { isPending, error, data } = usePaymentReviewOrders({
    ...search,
    page: search.page ?? 1,
    pageSize,
  })

  // The download is the O2C template, so it is deliberately not the generic orders export.
  const getCsvExport = () => queryClient.ensureQueryData(paymentReviewExportQueryOptions(search))
  // The report beside it: same filters, but every status and every column, for reading not re-upload.
  const getReportExport = () => queryClient.ensureQueryData(paymentReviewReportQueryOptions(search))

  const onSearch = (value: string) => {
    navigate({ search: { ...search, keyword: value || undefined, page: 1 } })
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread>
        <PageHeader>{t('paymentReview.heading')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={getReportExport}
            rowCount={data?.rowCount ?? 0}
            label={t('paymentReview.exportReport')}
          />
          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={data?.rowCount ?? 0}
            label={t('paymentReview.exportCsvTemplate')}
          />
          {/* Same permission the upload type itself is gated on in CORE_UPLOAD_TYPE_PERMISSIONS. */}
          {canUser('orders_payment_review_release') && <PaymentReviewUploadButton />}
        </HStack>
      </Spread>

      <Card>
        <CardContent className="py-6">
          <PaymentReviewDataTable
            isPending={isPending}
            error={error}
            data={data}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onAction={(operation, orders) => setAction({ operation, orders })}
          />
        </CardContent>
      </Card>

      <PaymentReviewActionDialog
        operation={action?.operation ?? null}
        orders={action?.orders ?? []}
        onClose={() => setAction(null)}
        onDone={() => setRowSelection({})}
      />
    </PageContainer>
  )
}
